import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import '@fontsource/oxanium/500.css';
import '@fontsource/oxanium/700.css';
import '@fontsource/oxanium/800.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import Header from './components/Header';
import Main from './components/Main';
import VideoBackground from './components/VideoBackground';
import MenuBar from './components/MenuBar';
import Referals from './components/Referals';
import Upgrades from './components/upgrades';
import Tasks from './components/Tasks';
import Top from './components/Top';
import Hub from './components/Hub';
import Duels from './components/Duels';
import DuelHud from './components/DuelHud';
import DuelResultLayer from './components/DuelResultLayer';
import AlertModal from './components/AlertModal';
import EngagementLayer from './components/EngagementLayer';
import api, { setToken, isSafeTelegramUrl, openSafeTelegramLink } from './api/client';
import { getTelegramUser } from './utils/telegramUser';
import { bgOverlayUrl, iconUrl, iconWebp } from './utils/assets';
import { LocaleProvider, useLocale } from './i18n/LocaleContext';
import { normalizeLang } from './i18n/translations';
import { useShakeBonus } from './hooks/useTelegramFeatures';
import { useGlobalEnergyRegen } from './hooks/useGlobalEnergyRegen';
import { applyLeagueChrome, applyTelegramSafeArea, ensureImmersive, setClosingConfirmation } from './utils/telegram';
import { celebrate } from './utils/fx';
import { mergeClickerState } from './utils/clickerMerge';
import { flushPendingTaps, getOutstandingTaps, subscribeTapBatches, awaitDrainPendingTaps } from './utils/tapFlush';
import { planetAtmosphere } from './utils/planetOrbit';
import './styles/main.css';
import './styles/refine.css';
import './styles/duels.css';

const SYNC_MIN_INTERVAL_MS = 4000;

const ClickerHome = ({ userData, clickerData, setClickerData, setSecurity, refreshClicker }) => {
  const { t } = useLocale();
  const [rubyHelp, setRubyHelp] = useState(false);
  const [atmHost, setAtmHost] = useState(null);
  const planetCode = clickerData?.equipped?.planet || 'none';
  const atm = planetAtmosphere(planetCode);

  useEffect(() => {
    // Mount inside .video-background (z-index 0) so energy/menu/header in
    // .page always paint above the wash — never compete with z-index hacks.
    setAtmHost(document.querySelector('.video-background'));
  }, []);

  useEffect(() => {
    const root = document.body;
    if (!atm) return undefined;
    const cls = `atm-${atm}`;
    // The skin's veil, grain, vignette and colour grade are pre-composited into
    // one plate per atmosphere, so the phone stops running four backdrop-reading
    // blends across the whole screen. `atm-baked` is what switches the CSS onto
    // that path — it goes on only once the plate URL is actually set, so a
    // missing file falls back to the original layer stack instead of a blank wash.
    root.style.setProperty('--bg-overlay-atm', `url(${bgOverlayUrl(atm)})`);
    root.classList.add('atm-active', 'atm-baked', cls);
    return () => {
      root.classList.remove('atm-active', 'atm-baked', cls);
      root.style.removeProperty('--bg-overlay-atm');
    };
  }, [atm]);

  const atmosphere = atm ? (
    <div className={`planet-atmosphere atm-${atm}`} aria-hidden>
      <span className="atm-veil" />
      <span className="atm-glow" />
      <span className="atm-grain" />
    </div>
  ) : null;

  return (
    <>
      {atmHost && atmosphere ? createPortal(atmosphere, atmHost) : null}
      <Header
        userData={userData}
        clickerData={clickerData}
        onRubyHelp={() => setRubyHelp(true)}
      />
      <Main
        clickerData={clickerData}
        onClickerUpdate={setClickerData}
        onSecurity={setSecurity}
        onRefresh={refreshClicker}
      />
      <AlertModal
        open={rubyHelp}
        icon="bulb.png"
        title={t('rubiesHelpTitle')}
        body={t('rubiesHelpBody')}
        onClose={() => setRubyHelp(false)}
        confirmLabel={t('gotIt')}
      />
    </>
  );
};

const BootCard = ({ children, error, onRetry, retryLabel = 'Retry' }) => (
  <div className="page boot-screen">
    <VideoBackground />
    <div className={`boot-card glass-panel soft-card ${error ? 'boot-error' : ''}`}>
      <div className="boot-orbit" aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <div className="boot-planet" aria-hidden />
      <div className="boot-logo">TAP MARS</div>
      <div className="boot-message">{children}</div>
      {!error ? (
        <>
          <div className="boot-spinner" aria-hidden />
          <div className="boot-progress" aria-hidden>
            <i />
          </div>
        </>
      ) : onRetry ? (
        <button type="button" className="action-btn soft-btn" style={{ marginTop: 14, width: '100%' }} onClick={onRetry}>
          {retryLabel}
        </button>
      ) : null}
    </div>
  </div>
);

/** Stable layout — EngagementLayer + MenuBar do not remount on route change. */
const AppLayout = ({ clickerData, setClickerData, refreshClicker }) => {
  const location = useLocation();

  // Energy fills on every tab the same way as on Main
  useGlobalEnergyRegen(clickerData, setClickerData);

  useEffect(() => {
    // Never show Telegram's "changes may be lost" on close
    setClosingConfirmation(false);
  }, []);

  useEffect(() => {
    // Orbit rings (forge / TON-618) hang past the viewport, so nothing may
    // scroll sideways. That is CSS's job: `touch-action: pan-y pinch-zoom` on
    // body stops a horizontal pan at the compositor and `overflow-x: clip`
    // stops an X scroller existing at all.
    //
    // There used to be a non-passive `touchmove` listener on document doing the
    // same thing in JS. A non-passive touch listener switches iOS off its
    // compositor scrolling fast path: every scroll frame has to round-trip
    // through the main thread first, which is both janky and the kind of
    // constant main-thread work that warms a phone up. The old scroll handler
    // also read scrollX, documentElement.scrollLeft and body.scrollLeft on
    // every scroll event — three forced layouts, up to 120 times a second on
    // ProMotion.
    //
    // What is left is one passive read, and only when a scroll event fires.
    const snapX = () => {
      if (window.scrollX) window.scrollTo(0, window.scrollY || 0);
    };
    window.addEventListener('scroll', snapX, { passive: true });
    return () => window.removeEventListener('scroll', snapX);
  }, []);

  useEffect(() => {
    // Kill Telegram BackButton on every route — TG re-shows it on nested paths
    try {
      const back = window.Telegram?.WebApp?.BackButton;
      back?.hide?.();
    } catch (_) {
      /* ignore */
    }
  }, [location.pathname]);

  useEffect(() => {
    // Soft reconcile on section change — debounced inside refreshClicker so
    // rapid tab switches do not stampede /sync/.
    refreshClicker({ preferRemoteBalance: true, reason: 'route' }).catch(() => {});
  }, [location.pathname, refreshClicker]);

  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshClicker({ preferRemoteBalance: true, reason: 'focus' }).catch(() => {});
      }
    };
    // Single hide flush for the whole app (Main must not also flush on hide).
    // visibilitychange + pagehide often fire together — debounce to one keepalive.
    let lastHideFlush = 0;
    const flushOnHide = () => {
      const now = Date.now();
      if (now - lastHideFlush < 800) return;
      lastHideFlush = now;
      flushPendingTaps({ keepalive: true }).catch(() => {});
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') flushOnHide();
      else refreshVisible();
    };
    const onStorage = (event) => {
      if (event.key === 'tapmars:balance-sync') refreshVisible();
    };
    window.addEventListener('focus', refreshVisible);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flushOnHide);
    window.addEventListener('tapmars:balance-sync', refreshVisible);
    return () => {
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flushOnHide);
      window.removeEventListener('tapmars:balance-sync', refreshVisible);
    };
  }, [refreshClicker]);

  useEffect(() => {
    applyLeagueChrome(clickerData?.season?.league);
  }, [clickerData?.season?.league]);

  const onShake = useCallback(async () => {
    if (!clickerData?.engagement?.shake_available) return;
    try {
      await awaitDrainPendingTaps({ timeoutMs: 12000 });
      const res = await api.claimShake();
      celebrate('level');
      setClickerData?.((prev) =>
        mergeClickerState(prev, res.clicker || {}, {
          preferRemoteBalance: true,
          engagement: {
            ...(prev?.engagement || {}),
            ...(res.clicker?.engagement || {}),
            shake_available: false,
          },
        })
      );
    } catch (_) {
      /* rate limited / unsupported */
    }
  }, [clickerData?.engagement?.shake_available, setClickerData]);

  useShakeBonus({
    enabled: Boolean(clickerData?.engagement?.shake_available),
    onShake,
  });

  return (
    <div className="page">
      <VideoBackground />
      <EngagementLayer
        engagement={clickerData?.engagement}
        offline={clickerData?.offline}
        setClickerData={setClickerData}
      />
      <Outlet />
      <DuelHud />
      <DuelResultLayer />
      <MenuBar />
    </div>
  );
};

const AppShell = ({ userData, clickerData, setClickerData, security, setSecurity, refreshClicker }) => {
  const { t } = useLocale();

  if (security && (security.status === 'frozen' || security.status === 'banned')) {
    const support = isSafeTelegramUrl(security.support_url)
      ? security.support_url
      : 'https://t.me/Tap_Mars_Support_bot';
    return (
      <BootCard error>
        <div>{security.message}</div>
        <div style={{ marginTop: 12 }}>Ticket: {security.ticket_id}</div>
        <button
          type="button"
          className="action-btn"
          style={{ marginTop: 16, width: '100%' }}
          onClick={() => openSafeTelegramLink(support)}
        >
          {t('support')}
        </button>
      </BootCard>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          element={
            <AppLayout
              clickerData={clickerData}
              setClickerData={setClickerData}
              refreshClicker={refreshClicker}
            />
          }
        >
          <Route path="/" element={<Navigate to="/clicker" replace />} />
          <Route
            path="/clicker"
            element={
              <ClickerHome
                userData={userData}
                clickerData={clickerData}
                setClickerData={setClickerData}
                setSecurity={setSecurity}
                refreshClicker={refreshClicker}
              />
            }
          />
          <Route
            path="/clicker/upgrade"
            element={
              <Upgrades
                clickerData={clickerData}
                onClickerUpdate={setClickerData}
                onRefresh={refreshClicker}
              />
            }
          />
          <Route
            path="/clicker/earn"
            element={<Tasks clickerData={clickerData} onClickerUpdate={setClickerData} />}
          />
          <Route path="/clicker/top" element={<Top />} />
          <Route
            path="/clicker/duel"
            element={<Duels clickerData={clickerData} refreshClicker={refreshClicker} />}
          />
          <Route path="/clicker/referals" element={<Referals />} />
          <Route path="/clicker/hub" element={<Hub userData={userData} clickerData={clickerData} onClickerUpdate={setClickerData} />} />
          <Route path="*" element={<Navigate to="/clicker" replace />} />
        </Route>
      </Routes>
    </Router>
  );
};

const App = () => {
  const [userData, setUserData] = useState(null);
  const [clickerData, setClickerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [security, setSecurity] = useState(null);
  const syncRequestRef = useRef(0);
  const lastFullSyncAtRef = useRef(0);
  const [uiLang, setUiLang] = useState(() => {
    const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    return normalizeLang(tgLang || 'ru');
  });

  const [bootKey, setBootKey] = useState(0);

  useEffect(() => {
    const onSec = (e) => {
      if (e?.detail) setSecurity(e.detail);
    };
    window.addEventListener('tapmars:security', onSec);
    return () => window.removeEventListener('tapmars:security', onSec);
  }, []);

  // Keep React balance in lockstep with each flush batch. Outstanding taps
  // shrink on settle; without this, shop liveBalance drains every ~0.8s.
  useEffect(() => {
    return subscribeTapBatches((data) => {
      if (data?.blocked) setSecurity(data.security);
      setClickerData((prev) =>
        mergeClickerState(prev, data, { preferRemoteBalance: true })
      );
    });
  }, []);

  const refreshClicker = useCallback(async (opts = {}) => {
    const requestId = ++syncRequestRef.current;
    const forceSync = opts.forceSync === true;
    const reason = opts.reason || 'manual';
    const preferRemote =
      opts.preferRemoteBalance === true ||
      opts.preferRemoteBalance === undefined;

    // Flush first. Never /sync/ while a drain is still in flight — that stamped
    // stale energy/balance over optimistic state and made tabs jump.
    const flushed = await flushPendingTaps();
    if (flushed && requestId === syncRequestRef.current) {
      if (flushed.blocked) setSecurity(flushed.security);
      else if (!flushed.busy) {
        setClickerData((prev) =>
          mergeClickerState(prev, flushed, { preferRemoteBalance: true })
        );
      }
    }

    if (requestId !== syncRequestRef.current) return flushed;

    // Route/focus: if queue remains, drain quietly and sync ONLY after empty.
    if (getOutstandingTaps() > 0 && !forceSync) {
      flushPendingTaps({ drain: true })
        .then(async (last) => {
          if (requestId !== syncRequestRef.current) return;
          if (last && !last.blocked) {
            setClickerData((prev) =>
              mergeClickerState(prev, last, { preferRemoteBalance: true })
            );
          }
          if (getOutstandingTaps() > 0) {
            const drained = await awaitDrainPendingTaps({ timeoutMs: 18000 });
            if (requestId !== syncRequestRef.current) return;
            if (drained.last && !drained.last.blocked) {
              setClickerData((prev) =>
                mergeClickerState(prev, drained.last, { preferRemoteBalance: true })
              );
            }
          }
          if (getOutstandingTaps() > 0) return;
          try {
            const data = await api.clickerSync();
            if (requestId !== syncRequestRef.current) return;
            lastFullSyncAtRef.current = Date.now();
            if (data.blocked) setSecurity(data.security);
            else setSecurity(null);
            setClickerData((prev) =>
              mergeClickerState(prev, data, { preferRemoteBalance: true })
            );
          } catch (_) {
            /* ignore */
          }
        })
        .catch(() => {});
      return flushed;
    }

    if (forceSync && getOutstandingTaps() > 0) {
      const drained = await awaitDrainPendingTaps({ timeoutMs: 20000 });
      if (requestId !== syncRequestRef.current) return drained.last;
      if (drained.last && !drained.last.blocked) {
        setClickerData((prev) =>
          mergeClickerState(prev, drained.last, { preferRemoteBalance: true })
        );
      }
    }

    const now = Date.now();
    const recentlySynced = now - lastFullSyncAtRef.current < SYNC_MIN_INTERVAL_MS;
    if (!forceSync && recentlySynced && flushed && getOutstandingTaps() <= 0) {
      return flushed;
    }
    if (!forceSync && recentlySynced && !flushed && reason !== 'boot') {
      return null;
    }

    const data = await api.clickerSync();
    if (requestId !== syncRequestRef.current) return data;
    lastFullSyncAtRef.current = Date.now();
    if (data.blocked) setSecurity(data.security);
    else setSecurity(null);
    setClickerData((prev) =>
      mergeClickerState(prev, data, {
        preferRemoteBalance: preferRemote,
      })
    );
    return data;
  }, []);

  const onLanguageChange = useCallback(async (language) => {
    setUiLang(language);
    try {
      await api.setLanguage(language);
    } catch (_) {
      /* offline / race — UI still updates */
    }
  }, []);

  useEffect(() => {
    applyTelegramSafeArea();
    // Drop any leftover perf-lite class from older builds
    document.body.classList.remove('perf-lite');
    // Always allow closing without Telegram's "unsaved changes" prompt
    setClosingConfirmation(false);
    const tg = window.Telegram?.WebApp;
    if (tg) {
      ensureImmersive();
      applyLeagueChrome('default');
      try {
        tg.BackButton?.hide?.();
      } catch (_) {
        /* ignore */
      }
    }

    // Warm the art the first screen paints. WebP is what <picture> will pick on
    // anything modern, so preload that and let the PNG stay a cold fallback.
    ['rocket', 'energy_new', 'coin', 'button', 'home', 'toolbox'].forEach((name) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = iconWebp(`${name}.png`) || iconUrl(`${name}.png`);
    });

    let cancelled = false;

    const extractInitData = () => {
      const webapp = window.Telegram?.WebApp;
      if (webapp?.initData) return webapp.initData;
      let found = '';
      try {
        const hash = window.location.hash.replace(/^#/, '');
        if (hash) {
          const params = new URLSearchParams(hash);
          const fromHash = params.get('tgWebAppData');
          if (fromHash) found = fromHash;
        }
        if (!found) {
          const q = new URLSearchParams(window.location.search);
          const fromQuery = q.get('tgWebAppData');
          if (fromQuery) found = fromQuery;
        }
        // Strip sensitive initData from the address bar after reading
        if (found && (window.location.hash || window.location.search.includes('tgWebAppData'))) {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('tgWebAppData');
            url.hash = '';
            window.history.replaceState(null, '', url.pathname + url.search);
          } catch (_) {
            /* ignore */
          }
        }
      } catch (_) {
        /* ignore */
      }
      return found;
    };

    const waitForInitData = async (timeoutMs = 14000) => {
      const start = Date.now();
      while (!cancelled && Date.now() - start < timeoutMs) {
        const webapp = window.Telegram?.WebApp;
        if (webapp) {
          try {
            webapp.ready?.();
            webapp.expand?.();
            webapp.BackButton?.hide?.();
          } catch (_) {
            /* ignore */
          }
        }
        const data = extractInitData();
        if (data) return data;
        await new Promise((r) => setTimeout(r, 80));
      }
      return extractInitData();
    };

    const authWithRetry = async (initData, attempts = 5) => {
      let lastErr;
      for (let i = 0; i < attempts; i += 1) {
        try {
          return await api.authInitData(initData);
        } catch (err) {
          lastErr = err;
          // Re-read initData in case Telegram refreshed it mid-boot
          const fresh = extractInitData() || initData;
          initData = fresh;
          await new Promise((r) => setTimeout(r, 280 * (i + 1)));
        }
      }
      throw lastErr;
    };

    async function bootstrap() {
      try {
        setLoading(true);
        setError(null);

        const initData = await waitForInitData(14000);
        if (!initData) {
          setToken(null);
          throw new Error('NEED_TG');
        }
        const auth = await authWithRetry(initData, 5);
        if (auth.blocked && !auth.access) {
          if (auth.security) setSecurity(auth.security);
          setLoading(false);
          return;
        }
        setToken(auth.access);
        if (auth.blocked && auth.security) setSecurity(auth.security);
        if (auth.ui_language) setUiLang(normalizeLang(auth.ui_language));

        const tgData = await api.meTelegram();
        if (cancelled) return;
        if (tgData.ui_language) setUiLang(normalizeLang(tgData.ui_language));
        const live = getTelegramUser();
        setUserData({
          ...tgData,
          // Prefer API-local /media/avatars — t.me/i/userpic often fails in mobile WebView.
          photo_url: tgData.photo_url || live?.photo_url || '',
          first_name: tgData.first_name || live?.first_name || '',
          last_name: tgData.last_name || live?.last_name || '',
          username: tgData.username || live?.username || '',
        });
        let clicker = null;
        let syncErr = null;
        for (let i = 0; i < 4; i += 1) {
          try {
            clicker = await api.clickerSync();
            syncErr = null;
            break;
          } catch (e) {
            syncErr = e;
            await new Promise((r) => setTimeout(r, 320 * (i + 1)));
          }
        }
        if (cancelled) return;
        if (!clicker) throw syncErr || new Error('SYNC_FAILED');
        if (clicker.blocked) setSecurity(clicker.security);
        setClickerData(clicker);
      } catch (err) {
        if (!cancelled) {
          // Keep token on transient post-auth failures so Retry doesn't force full re-auth
          if (err.message === 'NEED_TG') setToken(null);
          setError(err.message === 'NEED_TG' ? 'NEED_TG' : err.message || 'ERROR');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [bootKey]);

  const bootText = useMemo(() => {
    const dictNeed = {
      ru: 'Открой Tap Mars через бота @Tap_Mars_bot в Telegram (кнопка «Начать игру»).',
      en: 'Open Tap Mars via @Tap_Mars_bot in Telegram (“Start game”).',
    };
    const dictLoad = {
      ru: 'Готовим колонию на Марсе…',
      en: 'Preparing the Mars colony…',
    };
    const dictRetry = {
      ru: 'Повторить',
      en: 'Retry',
    };
    // Экран загрузки — первое, что видит игрок. Сырая строка от бэкенда
    // («Authorization failed») приходит на своём языке и ничего не подсказывает,
    // поэтому показываем локализованный текст с понятным следующим шагом.
    const dictFail = {
      ru: 'Не удалось войти. Проверь связь и попробуй ещё раз.',
      en: 'Sign-in failed. Check your connection and try again.',
    };
    if (loading) return { text: dictLoad[uiLang] || dictLoad.ru, retry: dictRetry[uiLang] || dictRetry.ru };
    if (error === 'NEED_TG') return { text: dictNeed[uiLang] || dictNeed.ru, retry: dictRetry[uiLang] || dictRetry.ru };
    return { text: dictFail[uiLang] || dictFail.ru, retry: dictRetry[uiLang] || dictRetry.ru };
  }, [loading, error, uiLang]);

  if (security && (security.status === 'frozen' || security.status === 'banned')) {
    return (
      <LocaleProvider initialLang={uiLang} onLanguageChange={onLanguageChange}>
        <AppShell
          userData={userData}
          clickerData={clickerData}
          setClickerData={setClickerData}
          security={security}
          setSecurity={setSecurity}
          refreshClicker={refreshClicker}
        />
      </LocaleProvider>
    );
  }

  if (loading || error) {
    return (
      <LocaleProvider initialLang={uiLang} onLanguageChange={onLanguageChange}>
        <BootCard
          error={Boolean(error)}
          retryLabel={bootText.retry}
          onRetry={
            error
              ? () => {
                  setError(null);
                  setLoading(true);
                  setBootKey((k) => k + 1);
                }
              : undefined
          }
        >
          {bootText.text}
        </BootCard>
      </LocaleProvider>
    );
  }

  return (
    <LocaleProvider initialLang={uiLang} onLanguageChange={onLanguageChange}>
      <AppShell
        userData={userData}
        clickerData={clickerData}
        setClickerData={setClickerData}
        security={security}
        setSecurity={setSecurity}
        refreshClicker={refreshClicker}
      />
    </LocaleProvider>
  );
};

export default App;
